import React, { useState, useEffect } from 'react';
import { Select as ReactSelect } from 'chakra-react-select';
import {
  Box,
  Button,
  Text,
  Input,
  Textarea,
  VStack,
  HStack,
  SimpleGrid,
  Checkbox,
  Alert,
  NativeSelect,
  IconButton,
  Dialog,
  Flex,
  Icon
} from '@chakra-ui/react';
import { FiTrash, FiEdit2 } from 'react-icons/fi';
import { FaPlus } from 'react-icons/fa';
import { LuContainer, LuPackage, LuPlane } from 'react-icons/lu';
import axios from 'axios';
import { dataService } from '../services/dataService';
import { branchDataService } from '../services/branchDataService';
import specialServiceOptionsService from '../services/specialServiceOptionsService';
import countryService from '../services/countryService';
import { config } from '../config';

const API_URL = config.BACKEND_URL;

const EditAgentModal = ({ agent, onClose, onSave, tableName = 'agentlist', dataType = 'main' }) => {
  const [formData, setFormData] = useState({
    country: '',
    agent_name: '',
    short_name: '',
    fcl: '',
    lcl: '',
    air: '',
    pic_overseas: '',
    email_pic: '',
    phone_number: '',
    manager: '',
    email_manager: '',
    phone_manager: '',
    mbl_information: '',
    status: '',
    contract: '',
    additional: '',
    network: '',
    city_port: '',
    class: '',
    special_service: '',
    remark: '',
    strongest_service: '',
    membership_validity: '',
    suspended: false,
    suspended_remark: '',
    consol_export: false,
    consol_import: false,
    consol_oversea: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [specialServiceOptions, setSpecialServiceOptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cityPorts, setCityPorts] = useState([]);
  const [selectedCityPorts, setSelectedCityPorts] = useState([]);

  // Inline create city port
  const [showAddCityPort, setShowAddCityPort] = useState(false);
  const [newCityPort, setNewCityPort] = useState({
    name: ''
  });
  const [newCityPortPics, setNewCityPortPics] = useState([
    { pic_name: '', pic_email: '', pic_phone: '' }
  ]);
  const [addingCityPort, setAddingCityPort] = useState(false);

  // State for PIC management of selected city ports
  const [cityPortPicsMap, setCityPortPicsMap] = useState({}); // { cityPortId: [pics] }
  const [editingPic, setEditingPic] = useState(null); // { cityPortId, picId, pic_name, pic_email, pic_phone }
  const [addingPicToCityPort, setAddingPicToCityPort] = useState(null); // cityPortId
  const [newPicData, setNewPicData] = useState({ pic_name: '', pic_email: '', pic_phone: '' });
  const [savingPic, setSavingPic] = useState(false);



  // Helper to check if a service is selected
  const isServiceSelected = (serviceName) => {
    if (!formData.strongest_service) return false;
    const services = formData.strongest_service.split(',').map(s => s.trim()).filter(s => s);
    return services.includes(serviceName);
  };

  // Helper to get array of selected services
  const getSelectedServicesArray = () => {
    if (!formData.strongest_service) return [];
    return formData.strongest_service.split(',').map(s => s.trim()).filter(s => s);
  };

  // Helper to check if a special service is selected
  const isSpecialServiceSelected = (serviceName) => {
    if (!formData.special_service) return false;
    const services = formData.special_service.split(';').map(s => s.trim()).filter(s => s);
    return services.includes(serviceName);
  };

  useEffect(() => {
    document.body.classList.add('modal-open');
    loadSpecialServiceOptions();
    loadCountries();
    loadCityPorts();
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  // Load city ports assigned to this agent
  useEffect(() => {
    if (agent?.id) {
      loadAgentCityPorts(agent.id);
    }
  }, [agent?.id]);

  const loadSpecialServiceOptions = async () => {
    try {
      const options = await specialServiceOptionsService.getAllOptions(true);
      setSpecialServiceOptions(options);
    } catch (error) {
      console.error('Error loading special service options:', error);
    }
  };

  const loadCountries = async () => {
    try {
      const result = await countryService.getAllCountries(true);
      setCountries(result);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadCityPorts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/city-ports`, {
        withCredentials: true
      });
      setCityPorts(response.data);
    } catch (error) {
      console.error('Error loading city ports:', error);
    }
  };

  const loadAgentCityPorts = async (agentId) => {
    try {
      const endpoint = dataType === 'branch'
        ? `${API_URL}/api/city-ports/branch-agent/${agentId}`
        : `${API_URL}/api/city-ports/agent/${agentId}`;
      const response = await axios.get(endpoint, {
        withCredentials: true
      });
      const ids = response.data.map(cp => cp.id);
      setSelectedCityPorts(ids);
      // Load PICs for these city ports
      loadPicsForCityPorts(ids);
    } catch (error) {
      console.error('Error loading agent city ports:', error);
    }
  };

  // Load PICs for selected city ports
  const loadPicsForCityPorts = async (cityPortIds) => {
    if (!cityPortIds || cityPortIds.length === 0) {
      setCityPortPicsMap({});
      return;
    }
    const picsMap = {};
    for (const cpId of cityPortIds) {
      try {
        const response = await axios.get(`${API_URL}/api/city-ports/${cpId}/pics`, {
          withCredentials: true
        });
        picsMap[cpId] = response.data;
      } catch (error) {
        console.error(`Error loading PICs for city port ${cpId}:`, error);
        picsMap[cpId] = [];
      }
    }
    setCityPortPicsMap(picsMap);
  };

  // Handle edit PIC
  const handleEditPic = (cityPortId, pic) => {
    setEditingPic({
      cityPortId,
      picId: pic.id,
      pic_name: pic.pic_name || '',
      pic_email: pic.pic_email || '',
      pic_phone: pic.pic_phone || ''
    });
  };

  // Save edited PIC
  const handleSavePic = async () => {
    if (!editingPic || !editingPic.pic_name.trim()) return;
    setSavingPic(true);
    try {
      await axios.put(`${API_URL}/api/city-ports/pics/${editingPic.picId}`, {
        pic_name: editingPic.pic_name,
        pic_email: editingPic.pic_email,
        pic_phone: editingPic.pic_phone
      }, { withCredentials: true });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${editingPic.cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [editingPic.cityPortId]: response.data }));
      setEditingPic(null);
    } catch (error) {
      console.error('Error saving PIC:', error);
      setError(error.response?.data?.error || 'Failed to save PIC');
    } finally {
      setSavingPic(false);
    }
  };

  // Add new PIC to existing city port
  const handleAddPicToExisting = async (cityPortId) => {
    if (!newPicData.pic_name.trim()) return;
    setSavingPic(true);
    try {
      await axios.post(`${API_URL}/api/city-ports/${cityPortId}/pics`, newPicData, {
        withCredentials: true
      });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [cityPortId]: response.data }));
      setAddingPicToCityPort(null);
      setNewPicData({ pic_name: '', pic_email: '', pic_phone: '' });
    } catch (error) {
      console.error('Error adding PIC:', error);
      setError(error.response?.data?.error || 'Failed to add PIC');
    } finally {
      setSavingPic(false);
    }
  };

  // Delete PIC
  const handleDeletePic = async (picId, cityPortId) => {
    if (!window.confirm('Are you sure you want to delete this PIC?')) return;
    try {
      await axios.delete(`${API_URL}/api/city-ports/pics/${picId}`, {
        withCredentials: true
      });

      // Refresh PICs for this city port
      const response = await axios.get(`${API_URL}/api/city-ports/${cityPortId}/pics`, {
        withCredentials: true
      });
      setCityPortPicsMap(prev => ({ ...prev, [cityPortId]: response.data }));
    } catch (error) {
      console.error('Error deleting PIC:', error);
      setError(error.response?.data?.error || 'Failed to delete PIC');
    }
  };

  const handleAddCityPort = async () => {
    if (!newCityPort.name.trim()) {
      return;
    }
    setAddingCityPort(true);
    try {
      // Filter out empty PICs, but ensure at least one if desired (optional)
      const picsToSend = newCityPortPics
        .filter(p => p.pic_name && p.pic_name.trim())
        .map(p => ({
          name: p.pic_name,
          email: p.pic_email,
          phone: p.pic_phone
        }));

      const payload = {
        name: newCityPort.name,
        pics: picsToSend
      };

      const response = await axios.post(`${API_URL}/api/city-ports`, payload, {
        withCredentials: true
      });
      const createdCityPort = response.data.cityPort;
      // Add to city ports list
      setCityPorts(prev => [...prev, createdCityPort].sort((a, b) => a.name.localeCompare(b.name)));
      // Auto-select the new city port
      setSelectedCityPorts(prev => [...prev, createdCityPort.id]);
      // Reset form
      setNewCityPort({ name: '' });
      setNewCityPortPics([{ pic_name: '', pic_email: '', pic_phone: '' }]);
      setShowAddCityPort(false);
    } catch (error) {
      console.error('Error adding city port:', error);
      setError(error.response?.data?.error || 'Failed to add city port');
    } finally {
      setAddingCityPort(false);
    }
  };



  useEffect(() => {
    if (agent) {
      setFormData({
        country: agent.country || '',
        agent_name: agent.agent_name || '',
        short_name: agent.short_name || '',
        fcl: agent.fcl || '',
        lcl: agent.lcl || '',
        air: agent.air || '',
        pic_overseas: agent.pic_overseas || '',
        email_pic: agent.email_pic || '',
        phone_number: agent.phone_number || '',
        manager: agent.manager || '',
        email_manager: agent.email_manager || '',
        phone_manager: agent.phone_manager || '',
        mbl_information: agent.mbl_information || '',
        status: agent.status || '',
        contract: agent.contract || '',
        additional: agent.additional || '',
        network: agent.network || '',
        city_port: agent.city_port || '',
        class: agent.class || '',
        special_service: agent.special_service || '',
        remark: agent.remark || '',
        strongest_service: agent.strongest_service || '',
        membership_validity: agent.membership_validity || '',
        suspended: agent.suspended || false,
        suspended_remark: agent.suspended_remark || '',
        consol_export: agent.consol_export || false,
        consol_import: agent.consol_import || false,
        consol_oversea: agent.consol_oversea || false
      });
    }
  }, [agent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'suspended_remark') {
        return { ...prev, [name]: value, suspended: true };
      }
      return {
        ...prev,
        [name]: value
      };
    });
  };

  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      country: selectedOption ? selectedOption.value : ''
    }));
  };

  const handleStrongestServiceCheckboxChange = (serviceName) => {
    const currentServices = getSelectedServicesArray();
    let updatedServices;
    if (currentServices.includes(serviceName)) {
      updatedServices = currentServices.filter(s => s !== serviceName);
    } else {
      updatedServices = [...currentServices, serviceName];
    }
    const servicesString = updatedServices.join(',');
    setFormData(prev => ({
      ...prev,
      strongest_service: servicesString
    }));
  };

  const handleSpecialServiceCheckboxChange = (serviceName) => {
    setFormData(prev => {
      const currentString = prev.special_service || '';
      const currentServices = currentString.split(';').map(s => s.trim()).filter(s => s);
      let updatedServices;
      if (currentServices.includes(serviceName)) {
        updatedServices = currentServices.filter(s => s !== serviceName);
      } else {
        updatedServices = [...currentServices, serviceName];
      }
      return {
        ...prev,
        special_service: updatedServices.join('; ')
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (formData.suspended && !formData.suspended_remark?.trim()) {
      setError('Suspended remark is required when Suspended is enabled');
      setLoading(false);
      return;
    }

    if (formData.short_name && formData.short_name.trim().length > 50) {
      setError('Short Name must be at most 50 characters');
      setLoading(false);
      return;
    }

    try {
      const dataToSubmit = {
        ...formData,
        email_pic: formData.email_pic || '',
        phone_number: formData.phone_number || '',
        manager: formData.manager || '',
        email_manager: formData.email_manager || '',
        phone_manager: formData.phone_manager || '',
        ...(agent?.inland !== undefined ? { inland: agent.inland } : {})
      };

      let response;
      // Choose service based on dataType and tableName
      const service = dataType === 'branch' ? branchDataService : dataService;

      if (tableName === 'blacklist') {
        if (dataType === 'branch') {
          response = await branchDataService.updateBlacklistAgent(agent.id, dataToSubmit);
        } else {
          response = await dataService.updateBlacklistAgent(agent.id, dataToSubmit);
        }
      } else {
        response = await service.updateAgent(agent.id, dataToSubmit);
      }

      // Assign city ports to agent
      if (tableName !== 'blacklist' && selectedCityPorts.length >= 0) {
        try {
          const assignEndpoint = dataType === 'branch'
            ? `${API_URL}/api/city-ports/branch-agent/${agent.id}/assign`
            : `${API_URL}/api/city-ports/agent/${agent.id}/assign`;
          await axios.post(assignEndpoint, {
            cityPortIds: selectedCityPorts
          }, { withCredentials: true });
        } catch (cpError) {
          console.error('Error assigning city ports:', cpError);
        }
      }

      if (onSave) {
        onSave(response.data.agent || response.data);
      }
      onClose();
    } catch (error) {

      console.error('Error updating agent:', error);
      setError(error.response?.data?.error || 'Failed to update agent data');
    } finally {
      setLoading(false);
    }
  };

  const countryOptions = countries.map(country => ({
    value: country.name,
    label: country.name
  }));

  return (
    <Dialog.Root open={true} onOpenChange={(e) => !e.open && onClose()}>
      <Dialog.Backdrop bg="blackAlpha.600" />
      <Dialog.Positioner>
        <Dialog.Content maxW="5xl" borderRadius="xl" mx={4} maxH="90vh" overflow="hidden">
          <Dialog.Header
            bg="blue.50"
            borderTopRadius="xl"
            borderBottom="1px"
            borderColor="gray.200"
            py={4}
            px={6}
          >
            <Flex justify="space-between" align="center" w="100%">
              <Text fontSize="lg" fontWeight="bold" color="blue.700">
                Edit Agent Information
              </Text>
            </Flex>
          </Dialog.Header>
          <Dialog.CloseTrigger />

          <Dialog.Body py={6} px={6} overflowY="auto">
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert.Root status="error" mb={4} borderRadius="md">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description>{error}</Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {/* Basic Information */}
                <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                  <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={4}>
                    Basic Information
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Agent Name *</Text>
                      <Input
                        name="agent_name"
                        value={formData.agent_name}
                        onChange={handleChange}
                        placeholder="Enter agent name"
                        required
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Country *</Text>
                      <ReactSelect
                        value={countryOptions.find(opt => opt.value === formData.country) || null}
                        onChange={handleCountryChange}
                        options={countryOptions}
                        placeholder="Select country..."
                        isClearable
                        isSearchable
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Short Name</Text>
                      <Input
                        name="short_name"
                        value={formData.short_name}
                        onChange={handleChange}
                        placeholder="Enter short name (optional, max 50)"
                        maxLength={50}
                      />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Network</Text>
                      <Input
                        name="network"
                        value={formData.network}
                        onChange={handleChange}
                        placeholder="Enter network"
                      />
                    </Box>
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text fontSize="sm" fontWeight="medium">City/Port</Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="purple"
                          onClick={() => setShowAddCityPort(!showAddCityPort)}
                        >
                          {showAddCityPort ? '✕ Cancel' : '+ Add New'}
                        </Button>
                      </Flex>

                      {/* Inline Add City Port Form */}
                      {/* Inline Add City Port Form */}
                      {showAddCityPort && (
                        <Box bg="purple.50" p={3} borderRadius="md" mb={2} border="1px dashed" borderColor="purple.300">
                          <Text fontWeight="bold" mb={2} color="purple.700" fontSize="sm">New City Port</Text>
                          <Input
                            size="sm"
                            placeholder="City Port Name *"
                            value={newCityPort.name}
                            onChange={(e) => setNewCityPort({ ...newCityPort, name: e.target.value })}
                            bg="white"
                            mb={3}
                          />

                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.600">PIC(s)</Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorPalette="purple"
                              onClick={() => setNewCityPortPics([...newCityPortPics, { pic_name: '', pic_email: '', pic_phone: '' }])}
                            >
                              <FaPlus style={{ marginRight: '4px' }} /> Add PIC
                            </Button>
                          </Flex>

                          <VStack spacing={2} align="stretch" mb={3}>
                            {newCityPortPics.map((pic, index) => (
                              <Flex key={index} gap={1} align="center">
                                <Input
                                  size="sm"
                                  placeholder="Name"
                                  value={pic.pic_name}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_name = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                <Input
                                  size="sm"
                                  placeholder="Email"
                                  value={pic.pic_email}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_email = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                <Input
                                  size="sm"
                                  placeholder="Phone"
                                  value={pic.pic_phone}
                                  onChange={(e) => {
                                    const updated = [...newCityPortPics];
                                    updated[index].pic_phone = e.target.value;
                                    setNewCityPortPics(updated);
                                  }}
                                  bg="white"
                                />
                                {newCityPortPics.length > 1 && (
                                  <IconButton
                                    aria-label="Remove"
                                    size="xs"
                                    colorPalette="red"
                                    variant="ghost"
                                    onClick={() => {
                                      if (newCityPortPics.length > 1) {
                                        setNewCityPortPics(newCityPortPics.filter((_, i) => i !== index));
                                      }
                                    }}
                                  >
                                    <FiTrash />
                                  </IconButton>
                                )}
                              </Flex>
                            ))}
                          </VStack>

                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={handleAddCityPort}
                            loading={addingCityPort}
                            disabled={!newCityPort.name.trim()}
                            variant="solid"
                            width="full"
                          >
                            Create & Select
                          </Button>
                        </Box>
                      )}

                      <ReactSelect
                        isMulti
                        value={cityPorts
                          .filter(cp => selectedCityPorts.includes(cp.id))
                          .map(cp => ({
                            value: cp.id,
                            label: cp.name,
                            picName: cp.pic_name,
                            picEmail: cp.pic_email
                          }))}
                        onChange={(selected) => {
                          const ids = selected ? selected.map(s => s.value) : [];
                          setSelectedCityPorts(ids);
                          loadPicsForCityPorts(ids);
                        }}
                        options={cityPorts.map(cp => ({
                          value: cp.id,
                          label: cp.name + (cp.pic_name ? ` (PIC: ${cp.pic_name})` : ''),
                          picName: cp.pic_name,
                          picEmail: cp.pic_email
                        }))}
                        placeholder="Select city/ports..."
                        isClearable
                        isSearchable
                        closeMenuOnSelect={false}
                        chakraStyles={{
                          multiValue: (provided) => ({
                            ...provided,
                            bg: 'purple.100',
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: 'purple.700',
                          }),
                        }}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Select existing or create new city ports with PIC info.
                      </Text>

                      {/* PIC Management for Selected City Ports */}
                      {selectedCityPorts.length > 0 && (
                        <Box mt={3} p={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                          <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={2}>
                            PICs of Selected City Ports
                          </Text>
                          <VStack spacing={3} align="stretch">
                            {selectedCityPorts.map(cpId => {
                              const cityPort = cityPorts.find(cp => cp.id === cpId);
                              const pics = cityPortPicsMap[cpId] || [];
                              return (
                                <Box key={cpId} p={2} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                                  <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="sm" fontWeight="semibold" color="purple.700">
                                      {cityPort?.name || `Port #${cpId}`}
                                    </Text>
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      colorPalette="purple"
                                      onClick={() => {
                                        setAddingPicToCityPort(addingPicToCityPort === cpId ? null : cpId);
                                        setNewPicData({ pic_name: '', pic_email: '', pic_phone: '' });
                                      }}
                                    >
                                      <FaPlus style={{ marginRight: '4px' }} /> Add PIC
                                    </Button>
                                  </Flex>

                                  {/* Add new PIC form */}
                                  {addingPicToCityPort === cpId && (
                                    <Box mb={2} p={2} bg="purple.50" borderRadius="md">
                                      <Flex gap={1} mb={2}>
                                        <Input
                                          size="sm"
                                          placeholder="Name *"
                                          value={newPicData.pic_name}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_name: e.target.value })}
                                          bg="white"
                                        />
                                        <Input
                                          size="sm"
                                          placeholder="Email"
                                          value={newPicData.pic_email}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_email: e.target.value })}
                                          bg="white"
                                        />
                                        <Input
                                          size="sm"
                                          placeholder="Phone"
                                          value={newPicData.pic_phone}
                                          onChange={(e) => setNewPicData({ ...newPicData, pic_phone: e.target.value })}
                                          bg="white"
                                        />
                                      </Flex>
                                      <HStack>
                                        <Button
                                          size="xs"
                                          colorPalette="purple"
                                          onClick={() => handleAddPicToExisting(cpId)}
                                          loading={savingPic}
                                          disabled={!newPicData.pic_name.trim()}
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="xs"
                                          variant="ghost"
                                          onClick={() => setAddingPicToCityPort(null)}
                                        >
                                          Cancel
                                        </Button>
                                      </HStack>
                                    </Box>
                                  )}

                                  {/* List of PICs */}
                                  {pics.length === 0 ? (
                                    <Text fontSize="xs" color="gray.500" fontStyle="italic">No PICs</Text>
                                  ) : (
                                    <VStack spacing={1} align="stretch">
                                      {pics.map(pic => (
                                        <Box key={pic.id}>
                                          {editingPic && editingPic.picId === pic.id ? (
                                            <Box p={2} bg="blue.50" borderRadius="md">
                                              <Flex gap={1} mb={2}>
                                                <Input
                                                  size="sm"
                                                  placeholder="Name *"
                                                  value={editingPic.pic_name}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_name: e.target.value })}
                                                  bg="white"
                                                />
                                                <Input
                                                  size="sm"
                                                  placeholder="Email"
                                                  value={editingPic.pic_email}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_email: e.target.value })}
                                                  bg="white"
                                                />
                                                <Input
                                                  size="sm"
                                                  placeholder="Phone"
                                                  value={editingPic.pic_phone}
                                                  onChange={(e) => setEditingPic({ ...editingPic, pic_phone: e.target.value })}
                                                  bg="white"
                                                />
                                              </Flex>
                                              <HStack>
                                                <Button
                                                  size="xs"
                                                  colorPalette="blue"
                                                  onClick={handleSavePic}
                                                  loading={savingPic}
                                                  disabled={!editingPic.pic_name.trim()}
                                                >
                                                  Save
                                                </Button>
                                                <Button
                                                  size="xs"
                                                  variant="ghost"
                                                  onClick={() => setEditingPic(null)}
                                                >
                                                  Cancel
                                                </Button>
                                              </HStack>
                                            </Box>
                                          ) : (
                                            <Flex
                                              justify="space-between"
                                              align="center"
                                              p={1}
                                              borderRadius="sm"
                                              _hover={{ bg: 'gray.100' }}
                                            >
                                              <Box>
                                                <Text fontSize="xs" fontWeight="medium">{pic.pic_name}</Text>
                                                <Text fontSize="xs" color="gray.500">
                                                  {[pic.pic_email, pic.pic_phone].filter(Boolean).join(' | ') || 'No contact info'}
                                                </Text>
                                              </Box>
                                              <HStack spacing={1}>
                                                <IconButton
                                                  aria-label="Edit"
                                                  size="xs"
                                                  variant="ghost"
                                                  colorPalette="blue"
                                                  onClick={() => handleEditPic(cpId, pic)}
                                                >
                                                  <FiEdit2 />
                                                </IconButton>
                                                <IconButton
                                                  aria-label="Delete"
                                                  size="xs"
                                                  variant="ghost"
                                                  colorPalette="red"
                                                  onClick={() => handleDeletePic(pic.id, cpId)}
                                                >
                                                  <FiTrash />
                                                </IconButton>
                                              </HStack>
                                            </Flex>
                                          )}
                                        </Box>
                                      ))}
                                    </VStack>
                                  )}
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Class</Text>
                      <Input
                        name="class"
                        value={formData.class}
                        onChange={handleChange}
                        placeholder="Enter class"
                      />
                    </Box>
                  </VStack>
                </Box>

                {/* Services */}
                <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                  <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={4}>
                    Services
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={3} spacing={3}>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>FCL</Text>
                        <Input name="fcl" value={formData.fcl} onChange={handleChange} placeholder="Yes/No" />
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>LCL</Text>
                        <Input name="lcl" value={formData.lcl} onChange={handleChange} placeholder="Yes/No" />
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>AIR</Text>
                        <Input name="air" value={formData.air} onChange={handleChange} placeholder="Yes/No" />
                      </Box>
                    </SimpleGrid>

                    <HStack spacing={6}>
                      <Checkbox.Root
                        checked={formData.consol_export}
                        onCheckedChange={(details) =>
                          setFormData(prev => ({ ...prev, consol_export: !!details.checked }))
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label fontSize="sm">Consol Export</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={formData.consol_import}
                        onCheckedChange={(details) =>
                          setFormData(prev => ({ ...prev, consol_import: !!details.checked }))
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label fontSize="sm">Consol Import</Checkbox.Label>
                      </Checkbox.Root>
                      <Checkbox.Root
                        checked={formData.consol_oversea}
                        onCheckedChange={(details) =>
                          setFormData(prev => ({ ...prev, consol_oversea: !!details.checked }))
                        }
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label fontSize="sm">Oversea</Checkbox.Label>
                      </Checkbox.Root>
                    </HStack>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2}>Special Service</Text>
                      <SimpleGrid columns={2} spacing={4} rowGap={3}>
                        {specialServiceOptions.length === 0 ? (
                          <Text fontSize="xs" color="gray.500" fontStyle="italic">No options available</Text>
                        ) : (
                          specialServiceOptions.map(option => (
                            <Checkbox.Root
                              key={option.id}
                              checked={isSpecialServiceSelected(option.option_name)}
                              onCheckedChange={() => handleSpecialServiceCheckboxChange(option.option_name)}
                            >
                              <Checkbox.HiddenInput />
                              <Checkbox.Control />
                              <Checkbox.Label fontSize="sm">{option.option_name}</Checkbox.Label>
                            </Checkbox.Root>
                          ))
                        )}
                      </SimpleGrid>
                    </Box>

                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={2} display="flex" alignItems="center" gap={2}>
                        ⭐ Strongest Services
                      </Text>
                      <VStack align="start" spacing={2}>
                        <Checkbox.Root
                          checked={isServiceSelected('FCL')}
                          onCheckedChange={() => handleStrongestServiceCheckboxChange('FCL')}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontSize="sm" display="flex" alignItems="center" gap={2}>
                            <Icon as={LuContainer} /> FCL (Full Container Load)
                          </Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root
                          checked={isServiceSelected('LCL')}
                          onCheckedChange={() => handleStrongestServiceCheckboxChange('LCL')}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontSize="sm" display="flex" alignItems="center" gap={2}>
                            <Icon as={LuPackage} /> LCL (Less Container Load)
                          </Checkbox.Label>
                        </Checkbox.Root>
                        <Checkbox.Root
                          checked={isServiceSelected('AIR')}
                          onCheckedChange={() => handleStrongestServiceCheckboxChange('AIR')}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label fontSize="sm" display="flex" alignItems="center" gap={2}>
                            <Icon as={LuPlane} /> AIR (Air Freight)
                          </Checkbox.Label>
                        </Checkbox.Root>
                      </VStack>
                    </Box>
                  </VStack>
                </Box>

                {/* Contact Information */}
                <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                  <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={4}>
                    Contact Information
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>PIC Overseas</Text>
                      <Input name="pic_overseas" value={formData.pic_overseas} onChange={handleChange} placeholder="Enter PIC name" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Email PIC</Text>
                      <Input name="email_pic" value={formData.email_pic} onChange={handleChange} placeholder="e.g., john@company.com" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Phone Number</Text>
                      <Input name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="Enter phone number" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>MBL Information</Text>
                      <Textarea name="mbl_information" value={formData.mbl_information} onChange={handleChange} rows={2} placeholder="Enter MBL info" />
                    </Box>
                    <Text fontSize="sm" fontWeight="bold" color="purple.600" mt={2}>Manager Info</Text>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Manager Name</Text>
                      <Input name="manager" value={formData.manager} onChange={handleChange} placeholder="Enter manager name" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Email Manager</Text>
                      <Input name="email_manager" value={formData.email_manager} onChange={handleChange} placeholder="Enter manager email" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Phone Manager</Text>
                      <Input name="phone_manager" value={formData.phone_manager} onChange={handleChange} placeholder="Enter manager phone" />
                    </Box>
                  </VStack>
                </Box>

                {/* Status & Additional */}
                <Box border="1px" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
                  <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={4}>
                    Status & Additional Info
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Status</Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field name="status" value={formData.status} onChange={handleChange}>
                          <option value="">Select status</option>
                          <option value="APPROVED">Approved</option>
                          <option value="POTENTIAL">Potential</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Contract</Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field name="contract" value={formData.contract} onChange={handleChange}>
                          <option value="">Select contract status</option>
                          <option value="YES">Yes</option>
                          <option value="NO">No</option>
                          <option value="PENDING">Pending</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Additional</Text>
                      <NativeSelect.Root>
                        <NativeSelect.Field name="additional" value={formData.additional} onChange={handleChange}>
                          <option value="">-- None --</option>
                          <option value="AGENT CONSIGNED">Agent Consigned</option>
                          <option value="QTM CONSIGNED">QTM Consigned</option>
                          <option value="BOTH SIDE">Both Side</option>
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Remark</Text>
                      <Textarea name="remark" value={formData.remark} onChange={handleChange} rows={3} placeholder="Enter remarks" />
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" mb={1}>Membership Validity</Text>
                      <Input type="date" name="membership_validity" value={formData.membership_validity || ''} onChange={handleChange} />
                      <Text fontSize="xs" color="gray.500" mt={1}>Select membership expiration date</Text>
                    </Box>
                    <Box>
                      <Checkbox.Root
                        checked={formData.suspended}
                        onCheckedChange={(details) =>
                          setFormData(prev => ({
                            ...prev,
                            suspended: !!details.checked,
                            suspended_remark: details.checked ? prev.suspended_remark : ''
                          }))
                        }
                        colorPalette="red"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control>
                          <Checkbox.Indicator />
                        </Checkbox.Control>
                        <Checkbox.Label>Suspended</Checkbox.Label>
                      </Checkbox.Root>
                    </Box>
                    {formData.suspended && (
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={1}>Suspended Remark *</Text>
                        <Textarea
                          name="suspended_remark"
                          value={formData.suspended_remark}
                          onChange={handleChange}
                          rows={2}
                          placeholder="Enter suspended remark"
                          required
                        />
                      </Box>
                    )}
                  </VStack>
                </Box>
              </SimpleGrid>
            </form>
          </Dialog.Body>

          <Dialog.Footer
            bg="gray.50"
            borderBottomRadius="xl"
            borderTop="1px"
            borderColor="gray.200"
            px={6}
            py={4}
          >
            <HStack spacing={3} w="100%" justify="flex-end">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                colorPalette="blue"
                onClick={handleSubmit}
                loading={loading}
                disabled={loading}
                loadingText="Saving..."
              >
                Save Changes
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default EditAgentModal;
